import React, { useState, useMemo } from 'react';
import { AppUser } from '../../types';
import { ONBOARDING_CITIES, ONBOARDING_ZONES } from '../../config/onboardingData';
import { CIEN_CUADRAS_BLUE, CIEN_CUADRAS_BUTTON_BLUE } from '../../firebase/firebaseConfig';
import { CienCuadrasLogoImg, SpinnerIcon, LogoutIcon } from '../../components/Icons';

interface OnboardingViewProps {
    appUser: AppUser;
    onCompleteOnboarding: (userId: string, data: any) => Promise<void>;
    onLogout?: () => void;
}

const ProgressBar: React.FC<{ current: number; total: number }> = ({ current, total }) => {
    const percentage = total > 0 ? (current / total) * 100 : 0;
    return (
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div 
                className="bg-sky-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${percentage}%` }}
            ></div>
        </div>
    );
};

// Helper component for formatted currency input
const CurrencyInput: React.FC<{
  id: string;
  name: 'min' | 'max';
  label: string;
  placeholder: string;
  value: string;
  onChange: (name: 'min' | 'max', value: string) => void;
  disabled?: boolean;
}> = ({ id, name, label, placeholder, value, onChange, disabled }) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-numeric characters to get the raw value
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    onChange(name, rawValue);
  };

  // Format the raw value for display with thousand separators
  const formattedValue = value ? new Intl.NumberFormat('es-CO').format(parseInt(value, 10)) : '';

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="mt-1 relative rounded-md shadow-sm">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-500 sm:text-sm">$</span>
        </div>
        <input
          type="text" // Use text type to allow for formatted string
          name={name}
          id={id}
          className="focus:ring-sky-500 focus:border-sky-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
          placeholder={placeholder}
          value={formattedValue}
          onChange={handleInputChange}
          disabled={disabled}
          inputMode="numeric" // Helps mobile users get a numeric keyboard
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <span className="text-gray-500 sm:text-sm">COP</span>
        </div>
      </div>
    </div>
  );
};


export const OnboardingView: React.FC<OnboardingViewProps> = ({ appUser, onCompleteOnboarding, onLogout }) => {
    const [step, setStep] = useState(0);
    const [selectedCities, setSelectedCities] = useState<string[]>([]);
    const [selectedZones, setSelectedZones] = useState<Record<string, string[]>>({});
    const [priceRange, setPriceRange] = useState({ min: '', max: '' });
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const zoneSteps = useMemo(() => selectedCities.map(city => ONBOARDING_ZONES[city]).filter(Boolean), [selectedCities]);
    const totalSteps = 1 + zoneSteps.length + 1; // 1 for cities, N for zones, 1 for price

    const handleNext = () => {
        // Validation for city selection
        if (step === 0 && selectedCities.length === 0) {
            setError("Debes seleccionar al menos una ciudad para continuar.");
            return;
        }
        setError(null);
        if (step < totalSteps - 1) {
            setStep(step + 1);
        }
    };

    const handlePrev = () => {
        setError(null);
        if (step > 0) {
            setStep(step - 1);
        }
    };

    const handleCityToggle = (city: string) => {
        setError(null);
        setSelectedCities(prev => {
            const newCities = prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city];
            // When a city is removed, also remove its zone data
            if (!newCities.includes(city)) {
                setSelectedZones(currentZones => {
                    const newZones = {...currentZones};
                    delete newZones[city];
                    return newZones;
                });
            }
            return newCities;
        });
    };
    
    const handleZoneToggle = (city: string, zone: string) => {
        setSelectedZones(prev => {
            const cityZones = prev[city] || [];
            const newZones = cityZones.includes(zone)
                ? cityZones.filter(z => z !== zone)
                : [...cityZones, zone];
            return { ...prev, [city]: newZones };
        });
    };

    const handlePriceChange = (name: 'min' | 'max', value: string) => {
        setPriceRange(prev => ({ ...prev, [name]: value }));
    };

    const handleFinish = async () => {
        const min = priceRange.min ? parseFloat(priceRange.min) : null;
        const max = priceRange.max ? parseFloat(priceRange.max) : null;

        if (min !== null && max !== null && min > max) {
            setError("El precio máximo no puede ser menor que el precio mínimo.");
            return;
        }
        setError(null);
        setIsSaving(true);
        try {
            await onCompleteOnboarding(appUser.uid, {
                commercializationCities: selectedCities,
                commercializationZones: selectedZones,
                priceRangeMin: min,
                priceRangeMax: max,
                onboardingCompleted: true,
            });
            // The view will change automatically when App.tsx re-renders
        } catch (e: any) {
            setError(e.message || "Ocurrió un error al guardar. Inténtalo de nuevo.");
            setIsSaving(false);
        }
    };

    const renderStepContent = () => {
        // Step 0: City Selection
        if (step === 0) {
            return (
                <div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">¿En qué ciudades de Colombia comercializas inmuebles?</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {ONBOARDING_CITIES.map(city => (
                            <label key={city} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-sky-50 transition-colors cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                    checked={selectedCities.includes(city)}
                                    onChange={() => handleCityToggle(city)}
                                />
                                <span className="text-sm font-medium text-gray-700">{city}</span>
                            </label>
                        ))}
                    </div>
                </div>
            );
        }

        // Zone selection steps
        if (step > 0 && step <= zoneSteps.length) {
            const currentZoneData = zoneSteps[step - 1];
            const currentCity = selectedCities[step - 1];
            return (
                <div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">{currentZoneData.question}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-80 overflow-y-auto pr-2">
                        {currentZoneData.zones.map(zone => (
                             <label key={zone} className="flex items-center space-x-3 p-2 border rounded-md hover:bg-sky-50 transition-colors cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                    checked={(selectedZones[currentCity] || []).includes(zone)}
                                    onChange={() => handleZoneToggle(currentCity, zone)}
                                />
                                <span className="text-xs font-medium text-gray-700">{zone}</span>
                            </label>
                        ))}
                    </div>
                </div>
            );
        }

        // Final step: Price Range
        if (step === totalSteps - 1) {
             return (
                <div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">¿Cuál es el rango de precios de los inmuebles que sueles comercializar?</h2>
                    <p className="text-sm text-gray-500 mb-6">En pesos colombianos (COP). Puedes dejar uno o ambos campos vacíos.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <CurrencyInput
                            id="priceMin"
                            name="min"
                            label="Precio Mínimo"
                            placeholder="190.000.000"
                            value={priceRange.min}
                            onChange={handlePriceChange}
                            disabled={isSaving}
                        />
                        <CurrencyInput
                            id="priceMax"
                            name="max"
                            label="Precio Máximo"
                            placeholder="500.000.000"
                            value={priceRange.max}
                            onChange={handlePriceChange}
                            disabled={isSaving}
                        />
                    </div>
                </div>
             );
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Top Banner with logged-in email and logout option */}
            {onLogout && (
                <div className="w-full bg-slate-800 text-white py-2.5 px-4 sm:px-6 flex justify-between items-center text-xs font-medium shadow-sm">
                    <div className="flex items-center space-x-2">
                        <span className="bg-slate-700 text-slate-300 px-2 py-0.5 rounded font-mono">Sesión:</span>
                        <span className="text-slate-100 font-semibold">{appUser.email || 'Sin correo'}</span>
                    </div>
                    <button 
                        onClick={onLogout}
                        className="flex items-center space-x-1.5 text-slate-300 hover:text-white hover:bg-slate-700/50 px-2.5 py-1 rounded-md transition-all active:scale-95"
                        aria-label="Cerrar sesión"
                    >
                        <LogoutIcon className="w-4 h-4 text-red-400" />
                        <span>Cerrar sesión</span>
                    </button>
                </div>
            )}
            
            <div className="flex-grow flex flex-col items-center justify-center px-2 py-8 sm:p-4">
                <div className="w-full max-w-4xl">
                     <div className="text-center mb-6">
                        <CienCuadrasLogoImg className="h-10 mx-auto" />
                        <h1 className="text-2xl font-bold mt-4" style={{color: CIEN_CUADRAS_BLUE}}>¡Hola, {appUser.displayName}!</h1>
                        <p className="text-gray-600">Completemos tu perfil para una mejor experiencia.</p>
                     </div>
                    <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-8">
                        <div className="mb-6">
                            <ProgressBar current={step + 1} total={totalSteps} />
                            <p className="text-sm text-center text-gray-500">Paso {step + 1} de {totalSteps}</p>
                        </div>

                        {error && (
                            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
                                <p>{error}</p>
                            </div>
                        )}

                        <div>
                            {renderStepContent()}
                        </div>
                        
                        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-between items-center">
                            <button
                                onClick={handlePrev}
                                disabled={step === 0 || isSaving}
                                className="px-6 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                            >
                                Anterior
                            </button>

                            {step < totalSteps - 1 ? (
                                <button
                                    onClick={handleNext}
                                    className="px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                                    style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                                >
                                    Siguiente
                                </button>
                            ) : (
                                <button
                                    onClick={handleFinish}
                                    disabled={isSaving}
                                    className="px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center disabled:opacity-50"
                                >
                                    {isSaving && <SpinnerIcon className="w-4 h-4 mr-2" />}
                                    {isSaving ? 'Guardando...' : 'Finalizar'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
